const defaultApiUrl = '/api';
const API_URL = (process.env.REACT_APP_API_URL || defaultApiUrl).replace(/\/$/, '');
const API_ORIGIN = /^https?:\/\//i.test(API_URL)
  ? API_URL.replace(/\/api\/?$/, '')
  : (typeof window !== 'undefined' ? window.location.origin : '');

const getStoredToken = () => {
  return localStorage.getItem('token') || sessionStorage.getItem('token');
};

const getAuthHeaders = () => {
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const saveToken = (token, remember = true) => {
  if (remember) {
    localStorage.setItem('token', token);
    sessionStorage.removeItem('token');
  } else {
    sessionStorage.setItem('token', token);
    localStorage.removeItem('token');
  }
};

export const clearToken = () => {
  localStorage.removeItem('token');
  sessionStorage.removeItem('token');
};

const isJsonContentType = (contentType = '') => /application\/json|\+json/i.test(contentType);

const isHtmlDocument = (body = '') => /^\s*<(?:!doctype|html)\b/i.test(body);

const parseResponseBody = async (response) => {
  if (response.status === 204 || response.status === 205) {
    return null;
  }

  const contentType = response.headers.get('content-type') || '';
  const text = await response.text();

  if (!text) {
    return null;
  }

  if (isJsonContentType(contentType)) {
    try {
      return JSON.parse(text);
    } catch (error) {
      throw new Error(`Ongeldige JSON ontvangen van ${response.url || 'de API'}. Controleer of de backend JSON terugstuurt.`);
    }
  }

  if (isHtmlDocument(text)) {
    if (response.status === 413) {
      throw new Error(
        `Upload te groot voor ${response.url || API_URL}. Verhoog de server/reverse-proxy uploadlimiet (bijv. nginx client_max_body_size) of kies een kleiner bestand.`
      );
    }

    throw new Error(
      `API-verzoek naar ${response.url || API_URL} gaf HTML terug in plaats van JSON. Controleer REACT_APP_API_URL of de reverse proxy voor /api.`
    );
  }

  return text;
};

const handleResponse = async (response) => {
  const data = await parseResponseBody(response);
  if (response.status === 401 && /token verlopen|ongeldige token|token/i.test(data?.error || data?.message || '')) {
    clearToken();
    window.location.href = '/login';
    throw new Error('Je sessie is verlopen. Log opnieuw in.');
  }
  if (!response.ok) {
    console.error('[API ERROR]', {
      url: response.url,
      status: response.status,
      statusText: response.statusText,
      body: data
    });
    throw new Error(data?.error || data?.message || data || 'Er is iets misgegaan');
  }
  return data;
};

const toAbsoluteApiUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  const normalized = url.startsWith('/') ? url : `/${url}`;
  return `${API_ORIGIN}${normalized}`;
};

const normalizeBlogPost = (post = {}) => {
  const media = Array.isArray(post.media)
    ? post.media.map((item) => ({
        ...item,
        url: toAbsoluteApiUrl(item.url)
      }))
    : [];

  return {
    ...post,
    media,
    imageUrl: toAbsoluteApiUrl(post.imageUrl) || media[0]?.url || null
  };
};

// Auth
export const authAPI = {
  login: async (email, password) => {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    return handleResponse(response);
  },
  
  register: async (userData) => {
    const response = await fetch(`${API_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(userData)
    });
    return handleResponse(response);
  }
};

// Users
export const usersAPI = {
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/users?${query}`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },
  
  getById: async (id) => {
    const response = await fetch(`${API_URL}/users/${id}`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },
  
  create: async (userData) => {
    const response = await fetch(`${API_URL}/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(userData)
    });
    return handleResponse(response);
  },

  enrollStudent: async (studentData) => {
    const response = await fetch(`${API_URL}/users/students/enroll`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(studentData)
    });
    return handleResponse(response);
  },
  
  update: async (id, userData) => {
    const response = await fetch(`${API_URL}/users/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(userData)
    });
    return handleResponse(response);
  },
  
  updateRole: async (id, role) => {
    const response = await fetch(`${API_URL}/users/${id}/role`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ role })
    });
    return handleResponse(response);
  },
  
  toggleStatus: async (id) => {
    const response = await fetch(`${API_URL}/users/${id}/status`, {
      method: 'PUT',
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },
  
  delete: async (id) => {
    const response = await fetch(`${API_URL}/users/${id}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },
  
  getStudents: async () => {
    const response = await fetch(`${API_URL}/users/role/students`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },
  
  getInstructors: async () => {
    const response = await fetch(`${API_URL}/users/role/instructors`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  }
};

// Studies
export const cohortsAPI = {
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/cohorts?${query}`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  create: async (data) => {
    const response = await fetch(`${API_URL}/cohorts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  update: async (id, data) => {
    const response = await fetch(`${API_URL}/cohorts/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_URL}/cohorts/${id}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  }
};

export const studiesAPI = {
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/studies?${query}`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },
  
  getById: async (id) => {
    const response = await fetch(`${API_URL}/studies/${id}`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },
  
  create: async (data) => {
    const response = await fetch(`${API_URL}/studies`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  
  update: async (id, data) => {
    const response = await fetch(`${API_URL}/studies/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  
  delete: async (id) => {
    const response = await fetch(`${API_URL}/studies/${id}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },
  
  // Study Years
  getYears: async (studyId) => {
    const response = await fetch(`${API_URL}/studies/${studyId}/years`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },
  
  createYear: async (studyId, data) => {
    const response = await fetch(`${API_URL}/studies/${studyId}/years`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  
  updateYear: async (studyId, yearId, data) => {
    const response = await fetch(`${API_URL}/studies/${studyId}/years/${yearId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  
  deleteYear: async (studyId, yearId) => {
    const response = await fetch(`${API_URL}/studies/${studyId}/years/${yearId}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },
  
  // Modules
  getModules: async (studyId, yearId) => {
    const response = await fetch(`${API_URL}/studies/${studyId}/years/${yearId}/modules`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },
  
  createModule: async (studyId, yearId, data) => {
    const response = await fetch(`${API_URL}/studies/${studyId}/years/${yearId}/modules`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  
  updateModule: async (studyId, yearId, moduleId, data) => {
    const response = await fetch(`${API_URL}/studies/${studyId}/years/${yearId}/modules/${moduleId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  
  deleteModule: async (studyId, yearId, moduleId) => {
    const response = await fetch(`${API_URL}/studies/${studyId}/years/${yearId}/modules/${moduleId}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  }
};

// Courses
export const coursesAPI = {
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/courses?${query}`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },
  
  getById: async (id) => {
    const response = await fetch(`${API_URL}/courses/${id}`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },
  
  create: async (data) => {
    const response = await fetch(`${API_URL}/courses`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  
  update: async (id, data) => {
    const response = await fetch(`${API_URL}/courses/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  
  delete: async (id) => {
    const response = await fetch(`${API_URL}/courses/${id}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },
  
  // Schedule
  getSchedule: async (courseId) => {
    const response = await fetch(`${API_URL}/courses/${courseId}/schedule`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },
  
  addSchedule: async (courseId, data) => {
    const response = await fetch(`${API_URL}/courses/${courseId}/schedule`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  
  updateSchedule: async (courseId, scheduleId, data) => {
    const response = await fetch(`${API_URL}/courses/${courseId}/schedule/${scheduleId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  
  deleteSchedule: async (courseId, scheduleId) => {
    const response = await fetch(`${API_URL}/courses/${courseId}/schedule/${scheduleId}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },
  
  getCalendarEvents: async () => {
    const response = await fetch(`${API_URL}/courses/calendar/events`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  addCalendarEvent: async (data) => {
    const response = await fetch(`${API_URL}/courses/calendar/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  addModuleToCourse: async (courseId, data) => {
    const response = await fetch(`${API_URL}/courses/${courseId}/modules`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  addExamPointToCourse: async (courseId, data) => {
    const response = await fetch(`${API_URL}/courses/${courseId}/exam-points`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  deleteModuleFromCourse: async (courseId, moduleId) => {
    const response = await fetch(`${API_URL}/courses/${courseId}/modules/${moduleId}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  deleteExamPointFromCourse: async (courseId, index) => {
    const response = await fetch(`${API_URL}/courses/${courseId}/exam-points/${index}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  }
};

// Enrollments
export const enrollmentsAPI = {
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/enrollments?${query}`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },
  
  getById: async (id) => {
    const response = await fetch(`${API_URL}/enrollments/${id}`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },
  
  create: async (data) => {
    const response = await fetch(`${API_URL}/enrollments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  
  update: async (id, data) => {
    const response = await fetch(`${API_URL}/enrollments/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  
  delete: async (id) => {
    const response = await fetch(`${API_URL}/enrollments/${id}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },
  
  bulkEnroll: async (data) => {
    const response = await fetch(`${API_URL}/enrollments/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  }
};

export const homeworkAPI = {
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/homework?${query}`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  getById: async (id) => {
    const response = await fetch(`${API_URL}/homework/${id}`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  create: async (data, file) => {
    const form = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (key === 'cardImageFile') return;
      if (value !== undefined && value !== null) {
        form.append(key, value);
      }
    });
    if (file) {
      form.append('attachment', file);
    }

    const response = await fetch(`${API_URL}/homework`, {
      method: 'POST',
      headers: { ...getAuthHeaders() },
      body: form
    });

    return handleResponse(response);
  },

  update: async (id, data, file) => {
    const form = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        form.append(key, value);
      }
    });
    if (file) {
      form.append('attachment', file);
    }

    const response = await fetch(`${API_URL}/homework/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeaders() },
      body: form
    });

    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_URL}/homework/${id}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  getSubmissions: async (homeworkId) => {
    const response = await fetch(`${API_URL}/homework/${homeworkId}/submissions`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  submit: async (homeworkId, file, comments) => {
    const form = new FormData();
    if (file) form.append('file', file);
    if (comments) form.append('comments', comments);

    const response = await fetch(`${API_URL}/homework/${homeworkId}/submissions`, {
      method: 'POST',
      headers: { ...getAuthHeaders() },
      body: form
    });
    return handleResponse(response);
  },

  gradeSubmission: async (homeworkId, submissionId, grade, feedback) => {
    const response = await fetch(`${API_URL}/homework/${homeworkId}/submissions/${submissionId}/grade`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ grade, feedback })
    });
    return handleResponse(response);
  },

  getAuditLogs: async (homeworkId) => {
    const response = await fetch(`${API_URL}/homework/${homeworkId}/audit-logs`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  downloadAttachmentUrl: (homeworkId) => `${API_URL}/homework/${homeworkId}/download`,
  viewAttachmentUrl: (homeworkId) => `${API_URL}/homework/${homeworkId}/view`,
  downloadSubmissionUrl: (homeworkId, submissionId) => `${API_URL}/homework/${homeworkId}/submissions/${submissionId}/download`,
  viewSubmissionUrl: (homeworkId, submissionId) => `${API_URL}/homework/${homeworkId}/submissions/${submissionId}/view`,
  downloadSubmissionsZipUrl: (homeworkId) => `${API_URL}/homework/${homeworkId}/submissions/zip`
};

export const gradesAPI = {
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/grades?${query}`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  getById: async (id) => {
    const response = await fetch(`${API_URL}/grades/${id}`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  create: async (data) => {
    const response = await fetch(`${API_URL}/grades`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  update: async (id, data) => {
    const response = await fetch(`${API_URL}/grades/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_URL}/grades/${id}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  }
};

export const paymentsAPI = {
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/payments?${query}`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  getById: async (id) => {
    const response = await fetch(`${API_URL}/payments/${id}`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  create: async (data) => {
    const response = await fetch(`${API_URL}/payments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  update: async (id, data) => {
    const response = await fetch(`${API_URL}/payments/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_URL}/payments/${id}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  }
};

export const reportsAPI = {
  getStudentProgress: async (studentId) => {
    const response = await fetch(`${API_URL}/reports/students/${studentId}/progress`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  getCourseGrades: async (courseId) => {
    const response = await fetch(`${API_URL}/reports/courses/${courseId}/grades`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  }
};

export const currenciesAPI = {
  getAll: async () => {
    const response = await fetch(`${API_URL}/currencies`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  create: async (data) => {
    const response = await fetch(`${API_URL}/currencies`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  update: async (id, data) => {
    const response = await fetch(`${API_URL}/currencies/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_URL}/currencies/${id}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  }
};

export const blogsAPI = {
  getPublic: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/blogs/public?${query}`);
    const data = await handleResponse(response);
    return {
      ...data,
      posts: Array.isArray(data.posts) ? data.posts.map(normalizeBlogPost) : []
    };
  },

  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/blogs?${query}`, {
      headers: { ...getAuthHeaders() }
    });
    const data = await handleResponse(response);
    return {
      ...data,
      posts: Array.isArray(data.posts) ? data.posts.map(normalizeBlogPost) : []
    };
  },

  create: async (data, imageFiles = []) => {
    let body;
    const headers = { ...getAuthHeaders() };
    const normalizedFiles = Array.isArray(imageFiles) ? imageFiles.filter(Boolean) : imageFiles ? [imageFiles] : [];

    if (normalizedFiles.length > 0) {
      const form = new FormData();
      Object.entries(data || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          form.append(key, value);
        }
      });
      normalizedFiles.forEach((file) => form.append('images', file));
      body = form;
    } else {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(data);
    }

    const response = await fetch(`${API_URL}/blogs`, {
      method: 'POST',
      headers,
      body
    });
    const result = await handleResponse(response);
    return normalizeBlogPost(result);
  },

  update: async (id, data, imageFiles = [], options = {}) => {
    let body;
    const headers = { ...getAuthHeaders() };
    const normalizedFiles = Array.isArray(imageFiles) ? imageFiles.filter(Boolean) : imageFiles ? [imageFiles] : [];
    const retainedImageIds = Array.isArray(options.retainedImageIds) ? options.retainedImageIds : null;

    if (normalizedFiles.length > 0 || retainedImageIds !== null) {
      const form = new FormData();
      Object.entries(data || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          form.append(key, value);
        }
      });
      if (retainedImageIds !== null) {
        form.append('retainedImageIds', JSON.stringify(retainedImageIds));
      }
      normalizedFiles.forEach((file) => form.append('images', file));
      body = form;
    } else {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(data);
    }

    const response = await fetch(`${API_URL}/blogs/${id}`, {
      method: 'PUT',
      headers,
      body
    });
    const result = await handleResponse(response);
    return normalizeBlogPost(result);
  },

  delete: async (id) => {
    const response = await fetch(`${API_URL}/blogs/${id}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  }
};

export const notificationsAPI = {
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/notifications?${query}`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  create: async (data) => {
    const response = await fetch(`${API_URL}/notifications`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  markRead: async (id) => {
    const response = await fetch(`${API_URL}/notifications/${id}/read`, {
      method: 'PUT',
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  processQueue: async (limit = 25) => {
    const response = await fetch(`${API_URL}/notifications/process-queue`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ limit })
    });
    return handleResponse(response);
  }
};

export const declarationsAPI = {
  getTemplates: async () => {
    const response = await fetch(`${API_URL}/declarations/templates`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  createTemplate: async (data) => {
    const response = await fetch(`${API_URL}/declarations/templates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  updateTemplate: async (id, data) => {
    const response = await fetch(`${API_URL}/declarations/templates/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  getRequests: async () => {
    const response = await fetch(`${API_URL}/declarations/requests`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  createRequest: async (data) => {
    const response = await fetch(`${API_URL}/declarations/requests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  reviewRequest: async (id, data) => {
    const response = await fetch(`${API_URL}/declarations/requests/${id}/review`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  getDocumentBlob: async (id, mode = 'view') => {
    const endpoint = mode === 'download'
      ? `${API_URL}/declarations/requests/${id}/document/download`
      : `${API_URL}/declarations/requests/${id}/document/view`;

    const response = await fetch(endpoint, {
      headers: { ...getAuthHeaders() }
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Document ophalen mislukt');
    }

    return response.blob();
  },

  viewDocumentUrl: (id) => `${API_URL}/declarations/requests/${id}/document/view`,
  downloadDocumentUrl: (id) => `${API_URL}/declarations/requests/${id}/document/download`
};

// Brochures
export const brochuresAPI = {
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/brochures?${query}`);
    return handleResponse(response);
  },

  getById: async (id) => {
    const response = await fetch(`${API_URL}/brochures/${id}`);
    return handleResponse(response);
  },

  create: async (data, file) => {
    const form = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (key === 'cardImageFile') return;
      if (value !== undefined && value !== null) {
        form.append(key, value);
      }
    });
    if (file) {
      form.append('file', file);
    }
    if (data?.cardImageFile) {
      form.append('cardImage', data.cardImageFile);
    }

    const response = await fetch(`${API_URL}/brochures`, {
      method: 'POST',
      headers: { ...getAuthHeaders() },
      body: form
    });

    return handleResponse(response);
  },

  update: async (id, data, file) => {
    const form = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (key === 'cardImageFile') return;
      if (value !== undefined && value !== null) {
        form.append(key, value);
      }
    });
    if (file) {
      form.append('file', file);
    }
    if (data?.cardImageFile) {
      form.append('cardImage', data.cardImageFile);
    }

    const response = await fetch(`${API_URL}/brochures/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeaders() },
      body: form
    });

    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_URL}/brochures/${id}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  downloadUrl: (id) => `${API_URL}/brochures/${id}/download`,
  viewUrl: (id) => `${API_URL}/brochures/${id}/view`,
  cardImageUrl: (id) => `${API_URL}/brochures/${id}/card-image`
};

export const contentAPI = {
  getPublic: async () => {
    const response = await fetch(`${API_URL}/content/public`);
    return handleResponse(response);
  },

  organogramImageUrl: () => `${API_URL}/content/public/organogram/image`,

  getPublicTeamMembers: async () => {
    const response = await fetch(`${API_URL}/content/public/team-members`);
    return handleResponse(response);
  },

  getPublicReviews: async () => {
    const response = await fetch(`${API_URL}/content/public/reviews`);
    return handleResponse(response);
  },

  getTeamMembers: async () => {
    const response = await fetch(`${API_URL}/content/team-members`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  getCommissieTypes: async () => {
    const response = await fetch(`${API_URL}/content/commissie-types`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  createCommissieType: async (data) => {
    const response = await fetch(`${API_URL}/content/commissie-types`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  deleteCommissieType: async (id) => {
    const response = await fetch(`${API_URL}/content/commissie-types/${id}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  createTeamMember: async (data, file) => {
    const form = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        form.append(key, value);
      }
    });
    if (file) {
      form.append('photo', file);
    }

    const response = await fetch(`${API_URL}/content/team-members`, {
      method: 'POST',
      headers: { ...getAuthHeaders() },
      body: form
    });
    return handleResponse(response);
  },

  updateTeamMember: async (id, data, file) => {
    const form = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        form.append(key, value);
      }
    });
    if (file) {
      form.append('photo', file);
    }

    const response = await fetch(`${API_URL}/content/team-members/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeaders() },
      body: form
    });
    return handleResponse(response);
  },

  deleteTeamMember: async (id) => {
    const response = await fetch(`${API_URL}/content/team-members/${id}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  teamMemberPhotoUrl: (id) => `${API_URL}/content/team-members/${id}/photo`,

  getReviews: async () => {
    const response = await fetch(`${API_URL}/content/reviews`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  createReview: async (data, file) => {
    const form = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        form.append(key, value);
      }
    });
    if (file) {
      form.append('photo', file);
    }

    const response = await fetch(`${API_URL}/content/reviews`, {
      method: 'POST',
      headers: { ...getAuthHeaders() },
      body: form
    });
    return handleResponse(response);
  },

  updateReview: async (id, data, file) => {
    const form = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        form.append(key, value);
      }
    });
    if (file) {
      form.append('photo', file);
    }

    const response = await fetch(`${API_URL}/content/reviews/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeaders() },
      body: form
    });
    return handleResponse(response);
  },

  deleteReview: async (id) => {
    const response = await fetch(`${API_URL}/content/reviews/${id}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  reviewPhotoUrl: (id) => `${API_URL}/content/reviews/${id}/photo`,

  getFlyers: async () => {
    const response = await fetch(`${API_URL}/content/flyers`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  createFlyer: async (data, file) => {
    const form = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        form.append(key, value);
      }
    });
    if (file) {
      form.append('image', file);
    }

    const response = await fetch(`${API_URL}/content/flyers`, {
      method: 'POST',
      headers: { ...getAuthHeaders() },
      body: form
    });
    return handleResponse(response);
  },

  updateFlyer: async (id, data, file) => {
    const form = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        form.append(key, value);
      }
    });
    if (file) {
      form.append('image', file);
    }

    const response = await fetch(`${API_URL}/content/flyers/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeaders() },
      body: form
    });
    return handleResponse(response);
  },

  deleteFlyer: async (id) => {
    const response = await fetch(`${API_URL}/content/flyers/${id}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  flyerImageUrl: (id) => `${API_URL}/content/flyers/${id}/image`
};

export const moduleMaterialsAPI = {
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/module-materials?${query}`, {
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  create: async (data, file) => {
    const form = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        form.append(key, value);
      }
    });
    if (file) {
      form.append('file', file);
    }

    const response = await fetch(`${API_URL}/module-materials`, {
      method: 'POST',
      headers: { ...getAuthHeaders() },
      body: form
    });
    return handleResponse(response);
  },

  update: async (id, data, file) => {
    const form = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        form.append(key, value);
      }
    });
    if (file) {
      form.append('file', file);
    }

    const response = await fetch(`${API_URL}/module-materials/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeaders() },
      body: form
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_URL}/module-materials/${id}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() }
    });
    return handleResponse(response);
  },

  viewFileUrl: (id) => `${API_URL}/module-materials/${id}/file/view`,
  downloadFileUrl: (id) => `${API_URL}/module-materials/${id}/file/download`
};

export const publicAPI = {
  submitRegistration: async (formData) => {
    const response = await fetch(`${API_URL}/public/registration`, {
      method: 'POST',
      body: formData
    });
    return handleResponse(response);
  }
};

export default API_URL;
